import PhotoCropDialog from "../components/hris/PhotoCropDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, idr } from "../lib/api";
import { Plus, Search, Pencil, Trash2, X, Upload, KeyRound, FileText, IdCard, Download, Camera, Loader2, ShieldCheck, UserCheck } from "lucide-react";
import { api as _api, API } from "../lib/api";
import EmployeeAvatar from "../components/hris/EmployeeAvatar";
import EmployeeAccountSection, { aturanAkunKaryawan } from "../components/hris/EmployeeAccountSection";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import { prepareEmployeePhoto, notifyEmployeePhotoChanged } from "../lib/employeePhoto";

const emptyEmp = {
  nik: "", nama: "", jk: "L", agama: "Islam", tgl_lahir: "", no_hp: "", email: "",
  status_pernikahan: "Lajang", jumlah_anak: 0, alamat_ktp: "", alamat_domisili: "",
  // Cabang KOSONG, wajib dipilih (keputusan owner 24 Sep 2026). Dulu terisi
  // "ZOA GROUP" — nama kantor pusat pemilik produk lama — dan karena nilai itu
  // tidak ada di daftar pilihan, <select> tampak memilih opsi pertama padahal
  // yang tersimpan tetap "ZOA GROUP".
  tanggal_masuk: "", branch: "", grade_jabatan: "", status_karyawan: "PKWT",
  no_rekening: "", nama_bank: "", no_bpjs_kesehatan: "", no_bpjs_tk: "", npwp: "", catatan: "",
  tunj_lainnya: 0, jatah_cuti: 12, jatah_skd: 12, divisi: "", is_active: true,
  kontak_darurat_nama: "", kontak_darurat_hubungan: "Orang Tua",
  kontak_darurat_hp: "", kontak_darurat_alamat: "",
  create_login_account: true, login_password: "",
  login_role: "employee", login_template_key: "",
};

// Diekspor supaya Layout.jsx bisa prefetch query yang sama persis saat kursor
// hover menu "Data Karyawan" cukup lama.
export const EMPLOYEES_QK = ["employees"];

const HUBUNGAN_DARURAT = ["Orang Tua", "Pasangan", "Anak", "Saudara Kandung", "Kerabat", "Teman", "Lainnya"];
const LOGIN_ROLES = [
  { v: "employee", l: "Employee (Karyawan)" },
  { v: "viewer", l: "Viewer (Read-only)" },
  { v: "admin", l: "Admin" },
  { v: "custom", l: "Custom (pakai template)" },
];

// Bank yang dipakai karyawan ZOA. Daftar tertutup, bukan ketikan bebas:
// "BCA", "bca", dan "Bank Central Asia" untuk orang yang sama membuat daftar
// transfer gaji harus dibaca manual. Kalau ada bank baru, tambahkan di sini.
const BANK_OPSI = [
  "BCA", "BNI", "BRI", "Mandiri", "BSI", "CIMB Niaga", "Permata",
  "Danamon", "BTN", "Panin", "OCBC", "Maybank", "Bank Kalbar", "SeaBank",
  "Jago", "Blu BCA Digital", "Neo Commerce", "Lainnya",
];


export default function Employees() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions();
  const queryClient = useQueryClient();
  // Satu bendera `canEdit = role !== "viewer"` dulu dipakai untuk SEMUA tombol di
  // halaman ini. Syarat itu hampir selalu benar, jadi tombolnya tampil untuk
  // hampir semua orang lalu berakhir 403 — persis yang dilarang aturan #3
  // CLAUDE.md. Sekarang tiap tombol memakai kunci modul + aksi yang SAMA PERSIS
  // dengan gerbang endpoint yang dipanggilnya:
  //   employees.create → POST /employees, /employees/import, .../create-account
  //   employees.update → PATCH /employees, .../photo, .../documents, .../id-card
  //   DELETE /employees dan GET /import-templates/{kind} masih role-only di
  //   backend, jadi gerbangnya di sini juga role — bukan can().
  const isAdminRole = user?.role === "admin" || user?.role === "superadmin";
  const canCreate = can("employees", "create");
  const canUpdate = can("employees", "update");
  const canDelete = user?.role === "superadmin";
  const [q, setQ] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [modal, setModal] = useState(null);
  const [docsModal, setDocsModal] = useState(null);
  const [credsModal, setCredsModal] = useState(null); // {email, password, role}
  const [uploadingPhotoId, setUploadingPhotoId] = useState(null);
  const photoBusy = useRef(false);
  const [photoSelection, setPhotoSelection] = useState(null);

  const { data: itemsData, error: itemsError } = useDataQuery(
    EMPLOYEES_QK, () => api.get("/employees").then(r => r.data),
  );
  const items = useMemo(() => itemsData || [], [itemsData]);
  useEffect(() => {
    if (itemsError) toast.error(itemsError?.response?.data?.detail || "Gagal memuat data karyawan.");
  }, [itemsError]);
  const invalidateEmployees = () => queryClient.invalidateQueries({ queryKey: EMPLOYEES_QK });

  const choosePhoto = employeeId => {
    if (photoBusy.current) return;
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = event => {
      const file = event.target.files?.[0];
      if (file && !photoBusy.current) setPhotoSelection({ file, employeeId });
    };
    input.click();
  };

  const savePhoto = async crop => {
    if (!photoSelection || photoBusy.current) return false;
    const { file, employeeId } = photoSelection;
    photoBusy.current = true; setUploadingPhotoId(employeeId);
    try {
      const photo = await prepareEmployeePhoto(file, crop);
      await _api.post(`/employees/${employeeId}/photo`, { content_base64: photo.content_base64 });
      notifyEmployeePhotoChanged(employeeId);
      toast.success(`Foto karyawan diperbarui (${Math.ceil(photo.bytes / 1024)} KB).`);
      invalidateEmployees();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Gagal mengunggah foto.");
      return false;
    } finally { photoBusy.current = false; setUploadingPhotoId(null); }
  };

  // Referensi (grade, shift, divisi, template izin) — jarang berubah dari
  // halaman ini, sengaja dicache; tidak ada aksi di halaman ini yang perlu
  // menginvalidasinya.
  const { data: gradesData, error: gradesError } = useDataQuery(
    ["grades"], () => api.get("/grades").then(r => r.data),
  );
  const grades = gradesData || [];
  useEffect(() => {
    if (gradesError) toast.error(gradesError?.response?.data?.detail || "Gagal memuat daftar grade jabatan.");
  }, [gradesError]);

  const { data: shiftsData, error: shiftsError } = useDataQuery(
    ["shifts"], () => api.get("/shifts").then(r => (r.data || []).filter(s => s.is_active !== false)),
  );
  const shifts = shiftsData || [];
  useEffect(() => {
    if (shiftsError) toast.error(shiftsError?.response?.data?.detail || "Gagal memuat daftar shift kerja.");
  }, [shiftsError]);

  // Divisi: kegagalannya dulu didiamkan (`.catch(() => {})`, tanpa toast),
  // sehingga dropdown Divisi tinggal kosong dan karyawan tersimpan tanpa divisi
  // tanpa seorang pun tahu ada yang gagal. Kegagalan senyap seperti ini yang
  // membuat orang mengira aplikasinya rusak; sekarang dilaporkan seperti grade
  // dan shift di atas.
  const { data: divisionsData, error: divisionsError } = useDataQuery(
    ["divisions"], () => api.get("/divisions").then(r => r.data || []),
  );
  const divisions = divisionsData || [];
  useEffect(() => {
    if (divisionsError) toast.error(divisionsError?.response?.data?.detail || "Gagal memuat daftar divisi.");
  }, [divisionsError]);

  // Grade menentukan gaji pokok, tunjangan, dan tarif lembur di slip gaji
  // (`server.py::_calc_slip` mencari koleksi `grades` lewat `grade_jabatan`).
  // Kalau daftarnya gagal dimuat ATAU memang kosong, satu-satunya nilai yang
  // bisa tersimpan adalah grade kosong — dan slipnya nanti nol tanpa pesan apa
  // pun. Form-nya ditolak terang-terangan, bukan dibiarkan menyimpan.
  const gradeSiap = !gradesError && grades.length > 0;

  const { data: permTemplatesData } = useDataQuery(
    ["permission-templates"], () => api.get("/permissions/templates").then(r => r.data?.templates || []),
  );
  const permTemplates = permTemplatesData || [];

  const filtered = useMemo(() => {
    return items.filter(e =>
      (!q || e.nama.toLowerCase().includes(q.toLowerCase()) || e.nik.includes(q)) &&
      (!filterBranch || e.branch === filterBranch)
    );
  }, [items, q, filterBranch]);
  // Urutan bawaan (backend: urut nama) dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();
  const sortedFiltered = sortRows(filtered, sortKey, sortDir);

  const save = async (form) => {
    try {
      if (form.id) {
        // Split login-related fields from employee body
        const { id, created_at, create_login_account, login_password,
                enable_login_account, login_password_reset, login_role,
                login_template_key, ...body } = form;
        // On EDIT, include only account fields that are meaningful
        if (enable_login_account) body.enable_login_account = true;
        if (login_password_reset) body.login_password_reset = login_password_reset;
        if (login_role) body.login_role = login_role;
        if (login_template_key !== undefined) body.login_template_key = login_template_key || null;
        const { data } = await api.patch(`/employees/${id}`, body);
        toast.success("Data karyawan diperbarui");
        if (data?._account?.password) {
          setCredsModal({ email: data._account.email, password: data._account.password, role: data._account.role });
        } else if (data?._account?.password_reset) {
          toast.success("Password login karyawan direset");
        }
      } else {
        const { data } = await api.post("/employees", form);
        if (data?._account?.password) {
          toast.success("Karyawan + akun login berhasil dibuat");
          setCredsModal({ email: data._account.email, password: data._account.password, role: data._account.role });
        } else if (data?._account?.error) {
          toast.warning(`Karyawan tersimpan, tapi akun login gagal: ${data._account.error}`);
        } else {
          toast.success("Karyawan ditambahkan");
        }
      }
      setModal(null); invalidateEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const del = async (id) => {
    if (!confirm("Hapus karyawan ini?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Terhapus"); invalidateEmployees(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      {photoSelection && <PhotoCropDialog file={photoSelection.file} onCancel={() => setPhotoSelection(null)} onConfirm={savePhoto} />}
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Data Karyawan</h1>
          <p className="mt-2 text-sm text-slate-500">Total {items.length} karyawan terdaftar</p>
        </div>
        {(canCreate || isAdminRole) && (
          <div className="flex gap-2">
            {/* GET /import-templates/{kind} (routers/imports.py) masih
                require_role("superadmin","admin"). Endpoint itu di luar berkas
                yang jadi tanggung jawab perbaikan ini, jadi gerbangnya di sini
                dibuat sama persis supaya tidak ada tombol yang ujungnya 403. */}
            {isAdminRole && (
            <button data-testid="btn-template-employees"
              onClick={async () => {
                const token = localStorage.getItem("hris_token");
                const res = await fetch(`${API}/import-templates/employees`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) { toast.error("Gagal download template"); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "Template_Karyawan.xlsx";
                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium">
              <Download className="w-4 h-4" /> Template
            </button>
            )}
            {canCreate && (
            <label data-testid="btn-import-excel"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium cursor-pointer transition-colors">
              <Upload className="w-4 h-4" /> Import Excel
              <input type="file" accept=".xlsx,.xls" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    const { data } = await _api.post("/employees/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Import selesai: ${data.created} baru, ${data.updated} update${data.errors.length ? ", " + data.errors.length + " error" : ""}`);
                    invalidateEmployees();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Gagal import"); }
                  e.target.value = "";
                }} />
            </label>
            )}
            {canCreate && (
            <button data-testid="btn-add-employee" onClick={() => setModal({ ...emptyEmp })}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium transition-colors active:scale-[.98]">
              <Plus className="w-4 h-4" /> Tambah Karyawan
            </button>
            )}
          </div>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q} onChange={e => setQ(e.target.value)}
              data-testid="employee-search"
              placeholder="Cari nama atau NIK..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40"
            />
          </div>
          <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)}
            data-testid="employee-filter-branch"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600/40">
            <option value="">Semua Branch</option>
            {BRANCHES.map(b => <option key={b}>{b}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="eh-mobile-records w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <SortTh label="NIK" sortKeyName="nik" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="emp-sort-nik" />
                <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="emp-sort-nama" />
                <SortTh label="Branch" sortKeyName="branch" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="emp-sort-branch" />
                <SortTh label="Grade Jabatan" sortKeyName="grade_jabatan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="emp-sort-grade" />
                <SortTh label="Status" sortKeyName="status_karyawan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="emp-sort-status" />
                <th className="text-left py-3 px-4">Masa Kerja</th>
                <th className="text-right py-3 px-4">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiltered.map(e => (
                <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td data-label="NIK" className="py-3 px-4 font-mono text-xs text-slate-600">{e.nik}</td>
                  <td className="eh-record-title py-3 px-4">
                    <div className="flex items-center gap-3">
                      <EmployeeAvatar employeeId={e.id} nama={e.nama} size={32} hasAvatar={e.has_avatar} />
                      <span className="font-medium text-slate-900">{e.nama}</span>
                    </div>
                  </td>
                  <td data-label="Cabang" className="py-3 px-4 text-slate-600">{e.branch}</td>
                  <td data-label="Grade jabatan" className="py-3 px-4 text-slate-600">{e.grade_jabatan}</td>
                  <td data-label="Status" className="py-3 px-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      e.status_karyawan === "PKWTT" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>{e.status_karyawan}</span>
                  </td>
                  {/* Dikirim server sudah jadi (field `masa_kerja`), dihitung
                      saat diminta — bukan disimpan (angka simpanan basi besok
                      pagi) dan bukan dihitung di sini (rumusnya harus satu,
                      karena Kartu Karyawan mencetaknya dari backend). */}
                  <td data-label="Masa kerja" className="py-3 px-4 text-slate-600 text-xs" data-testid={`emp-masa-kerja-${e.id}`}>
                    {e.masa_kerja || "-"}
                  </td>
                  <td className="eh-record-actions py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      {canUpdate && (
                        <button data-testid={`btn-photo-${e.id}`} onClick={() => choosePhoto(e.id)} disabled={!!uploadingPhotoId}
                          title={uploadingPhotoId === e.id ? "Mengompres dan mengunggah foto..." : "Upload foto karyawan (kompres otomatis)"}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900">
                          {uploadingPhotoId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        </button>
                      )}
                      {canCreate && (
                        <button data-testid={`btn-account-${e.id}`} onClick={async () => {
                          try {
                            const { data } = await _api.post(`/employees/${e.id}/create-account`);
                            setCredsModal({ email: data.email, password: data.password, name: e.nama, source: "create" });
                          } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
                        }} title="Buat akun portal login"
                          className="p-2 hover:bg-emerald-50 rounded-lg text-slate-500 hover:text-emerald-700">
                          <KeyRound className="w-4 h-4" />
                        </button>
                      )}
                      {canUpdate && (
                        <button data-testid={`btn-docs-${e.id}`} onClick={() => setDocsModal(e)} title="Lihat dokumen karyawan"
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900">
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      {canUpdate && (
                        <button data-testid={`btn-idcard-${e.id}`} onClick={async () => {
                          const token = localStorage.getItem("hris_token");
                          const res = await fetch(`${API}/employees/${e.id}/id-card`, { headers: { Authorization: `Bearer ${token}` } });
                          // Tanpa cek ini, badan error (JSON 403/404) ikut tersimpan
                          // sebagai berkas .pdf yang tidak bisa dibuka siapa pun.
                          if (!res.ok) { toast.error("Gagal membuat kartu karyawan"); return; }
                          const blob = await res.blob(); const url = URL.createObjectURL(blob);
                          const a = document.createElement("a"); a.href = url; a.download = `IDCard_${e.nama}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                        }} title="Cetak Kartu Karyawan"
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900">
                          <IdCard className="w-4 h-4" />
                        </button>
                      )}
                      {canUpdate && (
                        <button data-testid={`btn-edit-${e.id}`} aria-label={`Edit ${e.nama}`} onClick={() => setModal({ ...e })} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-900">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button data-testid={`btn-del-${e.id}`} aria-label={`Hapus ${e.nama}`} onClick={() => del(e.id)} className="p-2 hover:bg-rose-50 rounded-lg transition-colors text-slate-500 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-500">Tidak ada data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <EmployeeModal
          data={modal} grades={grades} gradeSiap={gradeSiap} shifts={shifts} employees={items}
          permTemplates={permTemplates} pelaku={user}
          divisions={divisions}
          onClose={() => setModal(null)} onSave={save}
        />
      )}
      {docsModal && (
        <EmpDocsModal emp={docsModal} onClose={() => setDocsModal(null)} />
      )}
      {credsModal && (
        <CredsModal creds={credsModal} onClose={() => setCredsModal(null)} />
      )}
    </div>
  );
}

function EmpDocsModal({ emp, onClose }) {
  const [docs, setDocs] = useState([]);
  // Tanpa .catch, panggilan yang gagal jadi unhandled rejection: modalnya tetap
  // terbuka dan menulis "belum upload dokumen apa pun" — pengguna menyimpulkan
  // karyawannya memang tidak punya dokumen, padahal permintaannya ditolak.
  useEffect(() => {
    _api.get(`/employees/${emp.id}/documents`)
      .then(r => setDocs(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat dokumen karyawan."));
  }, [emp.id]);
  const dl = async (id, fname) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/employees/${emp.id}/documents/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal mengunduh dokumen"); return; }
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Dokumen milik</p>
            <h3 className="font-display font-semibold text-slate-900">{emp.nama}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          {docs.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Karyawan belum upload dokumen apa pun
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                <th className="text-left py-2 px-2">Jenis</th>
                <th className="text-left py-2 px-2">File</th>
                <th className="text-right py-2 px-2">Size</th>
                <th className="text-right py-2 px-2"></th>
              </tr></thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="py-2 px-2"><span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">{d.nama}</span></td>
                    <td className="py-2 px-2 text-slate-700 text-sm">{d.file_name}</td>
                    <td className="py-2 px-2 text-right text-slate-500 text-xs">{(d.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="py-2 px-2 text-right">
                      <button onClick={() => dl(d.id, d.file_name)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900"><Download className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EmployeeModal({ data, grades, gradeSiap = true, shifts = [], employees = [], permTemplates = [], divisions = [], pelaku = null, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const BRANCHES = useCabangOptions();
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const isEdit = !!form.id;
  // Email karyawan = email LOGIN akunnya (disinkron server). Untuk akun yang
  // tidak boleh disentuh pengguna ini (E29) medannya dikunci — kalau dibiarkan,
  // mengganti email berujung 403 dari server (aturan #3).
  const { akunTerlindungi, akunSuperadmin } = aturanAkunKaryawan({ form, isEdit, pelaku });
  const suggestedPwd = (form.nik || "").slice(-6);
  // Grade yang sudah tersimpan tapi tidak ada lagi di master tetap ditampilkan
  // dengan penanda. Kalau tidak, membuka Edit lalu menekan Simpan diam-diam
  // mengosongkan grade karyawan itu — dan slip gajinya ikut jadi nol.
  const gradeTersimpan = (form.grade_jabatan || "").trim();
  const opsiGrade = grades.map(g => ({ v: g.grade_jabatan, l: g.grade_jabatan }));
  if (gradeTersimpan && !opsiGrade.some(o => o.v === gradeTersimpan)) {
    opsiGrade.unshift({ v: gradeTersimpan, l: `${gradeTersimpan} (nilai lama)` });
  }
  // Pola yang sama untuk cabang: cabang tersimpan yang tidak ada di daftar tetap
  // ditawarkan apa adanya, supaya membuka Edit lalu Simpan tidak diam-diam
  // memindahkan karyawan ke cabang lain.
  const cabangTersimpan = (form.branch || "").trim();
  const opsiCabang = BRANCHES.map(b => ({ v: b, l: b }));
  if (cabangTersimpan && !opsiCabang.some(o => o.v === cabangTersimpan)) {
    opsiCabang.unshift({ v: cabangTersimpan, l: `${cabangTersimpan} (nilai lama)` });
  }
  // Menyimpan DIBLOKIR hanya kalau hasilnya benar-benar karyawan tanpa grade:
  // daftar grade tidak tersedia DAN karyawan ini belum punya grade tersimpan.
  // Mengedit karyawan lama yang grade-nya sudah terisi tetap boleh — kalau ikut
  // diblokir, satu daftar grade yang gagal dimuat mengunci seluruh halaman
  // Data Karyawan, dan itu menukar satu masalah dengan masalah yang lebih besar.
  const bolehSimpan = gradeSiap || !!gradeTersimpan;
  const hasAccount = !!form.has_login_account;
  const showAccountSection = !isEdit ? true : (hasAccount || form.enable_login_account);
  // Form terpanjang di modul HRIS (puluhan field lintas 4 seksi) — kehilangan
  // isian karena Escape/klik-di-luar tanpa sengaja paling mahal terjadi di sini.
  const guard = useModalGuard({
    onClose, dangerous: true, disabled: !bolehSimpan,
    onSubmit: () => { if (bolehSimpan) onSave(form); },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="font-display text-lg font-semibold text-slate-900">
            {isEdit ? "Edit Karyawan" : "Tambah Karyawan"}
          </h3>
          <button onClick={guard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-6 space-y-6" data-testid="employee-form">
          {!gradeSiap && (
            <div data-testid="peringatan-grade-kosong"
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <b>Daftar grade jabatan tidak tersedia.</b> Grade menentukan gaji pokok,
              tunjangan, dan tarif lembur di slip gaji — menyimpan karyawan tanpa grade
              membuat slipnya nol tanpa peringatan apa pun. Minta admin mengisi Master Data ›
              Grade Gaji (atau memberi Anda izin membacanya), lalu buka form ini lagi.
              {gradeTersimpan && " Grade yang sudah tersimpan di karyawan ini tetap dipertahankan."}
            </div>
          )}
          <Section title="Informasi Pribadi">
            <Field label="NIK *"><Input value={form.nik} onChange={v => upd("nik", v)} required tid="input-nik" /></Field>
            <Field label="Nama Lengkap *"><Input value={form.nama} onChange={v => upd("nama", v)} required tid="input-nama" /></Field>
            <Field label="Jenis Kelamin">
              <Select value={form.jk} onChange={v => upd("jk", v)} tid="input-jk"
                options={[{v:"L",l:"Laki-laki"},{v:"P",l:"Perempuan"}]} />
            </Field>
            <Field label="Agama">
              <Select value={form.agama} onChange={v => upd("agama", v)} tid="input-agama"
                options={["Islam","Kristen","Katolik","Hindu","Buddha","Konghucu"].map(x=>({v:x,l:x}))} />
            </Field>
            <Field label="Tanggal Lahir"><DatePicker value={form.tgl_lahir?.slice(0,10) || ""} onChange={v => upd("tgl_lahir", v)} data-testid="input-tgl-lahir" /></Field>
            <Field label="No. HP"><Input value={form.no_hp} onChange={v => upd("no_hp", v)} /></Field>
            <Field label="Email">
              {akunTerlindungi ? (
                <p className="text-sm text-slate-700 py-2" data-testid="input-email-terkunci">
                  {form.email || "—"}
                  <span className="block text-xs text-slate-500">
                    Email ini juga email login akunnya — hanya bisa diubah {akunSuperadmin ? "superadmin" : "superadmin atau admin"}.
                  </span>
                </p>
              ) : (
                <Input type="email" value={form.email} onChange={v => upd("email", v)} />
              )}
            </Field>
            <Field label="Status Pernikahan">
              <Select value={form.status_pernikahan} onChange={v => upd("status_pernikahan", v)}
                options={["Lajang","Menikah","Cerai"].map(x=>({v:x,l:x}))} />
            </Field>
            <Field label="Jumlah Anak"><Input type="number" value={form.jumlah_anak} onChange={v => upd("jumlah_anak", parseInt(v)||0)} /></Field>
          </Section>

          <Section title="Informasi Kerja">
            <Field label="Tanggal Masuk"><DatePicker value={form.tanggal_masuk?.slice(0,10) || ""} onChange={v => upd("tanggal_masuk", v)} data-testid="input-tanggal-masuk" /></Field>
            <Field label="Branch *">
              <Select value={form.branch} onChange={v => upd("branch", v)} tid="input-branch" required
                options={[{ v: "", l: "— pilih cabang —" }, ...opsiCabang]} />
            </Field>
            <Field label="Grade Jabatan *">
              <Select value={form.grade_jabatan} onChange={v => upd("grade_jabatan", v)} tid="input-grade" required
                options={[{v:"",l:"— pilih —"}, ...opsiGrade]} />
            </Field>
            <Field label="Divisi">
              <Select value={form.divisi || ""} onChange={v => upd("divisi", v)} tid="input-divisi"
                options={[
                  { v: "", l: divisions.length === 0 ? "— belum ada divisi (buat di Master Divisi) —" : "— pilih divisi —" },
                  ...divisions.map(d => ({ v: d.nama, l: `${d.nama}${d.branch ? ` · ${d.branch}` : ""}` })),
                ]} />
            </Field>
            <Field label="Status Karyawan">
              <Select value={form.status_karyawan} onChange={v => upd("status_karyawan", v)}
                options={[{v:"PKWT",l:"PKWT"},{v:"PKWTT",l:"PKWTT"},{v:"Magang",l:"Magang"}]} />
            </Field>
            <Field label="Tgl Habis Kontrak (PKWT)"><DatePicker value={form.tgl_habis_kontrak?.slice(0,10) || ""} onChange={v => upd("tgl_habis_kontrak", v)} data-testid="input-tgl-habis-kontrak" /></Field>
            <Field label="Shift Kerja Default">
              <Select value={form.default_shift_id || ""} onChange={v => upd("default_shift_id", v || null)} tid="input-default-shift"
                options={[
                  { v: "", l: shifts.length === 0 ? "— belum ada shift (buat di menu Master › Shift Kerja) —" : "— tidak diset (pakai konfig default) —" },
                  ...shifts.map(s => ({ v: s.id, l: `${s.name} · ${s.jam_masuk} – ${s.jam_pulang} · tol ${s.toleransi_menit}m` })),
                ]} />
            </Field>
            <Field label="Tunjangan Lainnya (Rp)">
              <InputRupiah value={form.tunj_lainnya} onChange={n => upd("tunj_lainnya", n)}
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
            </Field>
            <Field label="Jatah Cuti / thn"><Input type="number" value={form.jatah_cuti} onChange={v => upd("jatah_cuti", parseInt(v)||12)} /></Field>
            <Field label="Jatah SKD / thn (Surat Dokter)"><Input type="number" value={form.jatah_skd || 0} onChange={v => upd("jatah_skd", parseInt(v)||0)} tid="input-jatah-skd" /></Field>
            <Field label="Target Poin / bln (Sales)"><Input type="number" value={form.poin_target || 0} onChange={v => upd("poin_target", parseInt(v)||0)} /></Field>
            <Field label="Bobot Rp / Poin (Sales)">
              <InputRupiah value={form.bobot_per_poin || 0} onChange={n => upd("bobot_per_poin", n)}
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
            </Field>
            <Field label="Target Upselling (%)"><Input type="number" value={form.target_upselling_pct || 0} onChange={v => upd("target_upselling_pct", parseFloat(v)||0)} tid="input-target-upselling" /></Field>
            <Field label="Denda Pulang Awal / jam (Rp)">
              {/* bolehKosong: di sini KOSONG dan NOL berbeda artinya — kosong =
                  ikut setelan global, 0 = karyawan ini memang tidak didenda.
                  Tanpa itu, pengecualian "bebas denda" per orang mustahil dibuat. */}
              <InputRupiah value={form.denda_pulang_awal_per_jam ?? ""} testid="input-denda-pulang-awal"
                bolehKosong
                placeholder="kosong = ikut setelan global"
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40"
                onChange={n => upd("denda_pulang_awal_per_jam", n)} />
              <span className="block text-[10px] text-slate-400 mt-1">
                Kosongkan untuk mengikuti setelan global di Master Data.
              </span>
            </Field>
            <Field label="Atasan / Reporting Line" className="sm:col-span-2">
              <Select value={form.atasan_id || ""} onChange={v => upd("atasan_id", v || null)} tid="input-atasan"
                options={[
                  { v: "", l: "— tanpa atasan (top level) —" },
                  ...employees.filter(e => e.id !== form.id).map(e => ({
                    v: e.id, l: `${e.nama}${e.grade_jabatan ? ` · ${e.grade_jabatan}` : ""}${e.branch ? ` · ${e.branch}` : ""}`
                  }))
                ]} />
            </Field>
          </Section>

          <Section title="Dokumen">
            {/* Nama bank dipisah dari nomornya (brief owner 29 Agu) —
                    tanpa kolom sendiri, bank ikut diketik di kolom nomor
                    dengan format berbeda tiap orang. */}
            <Field label="Nama Bank">
              <select value={form.nama_bank || ""} data-testid="emp-nama-bank"
                onChange={e => upd("nama_bank", e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">— pilih bank —</option>
                {BANK_OPSI.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="No. Rekening"><Input value={form.no_rekening} onChange={v => upd("no_rekening", v)} /></Field>
            <Field label="Alamat KTP">
              <Input value={form.alamat_ktp} data-testid="emp-alamat-ktp"
                onChange={v => upd("alamat_ktp", v)} />
            </Field>
            <Field label="Alamat Domisili">
              <Input value={form.alamat_domisili} data-testid="emp-alamat-domisili"
                onChange={v => upd("alamat_domisili", v)} />
            </Field>
            {/* Nonaktifkan karyawan yang sudah resign (brief owner 29 Agu).
                SENGAJA bukan tombol hapus: slip gaji, absensi, dan nota yang
                pernah ia buat tetap harus bisa ditelusuri. Yang berubah cuma
                ia tidak lagi muncul di daftar aktif. */}
            <Field label="Status Kepegawaian">
              <label className="flex items-center gap-2 h-9 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.is_active !== false}
                  data-testid="emp-aktif"
                  onChange={e => upd("is_active", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600" />
                {form.is_active !== false ? "Aktif" : "Nonaktif (resign) — riwayatnya tetap tersimpan"}
              </label>
            </Field>
            <Field label="NPWP"><Input value={form.npwp} onChange={v => upd("npwp", v)} /></Field>
            <Field label="BPJS Kesehatan"><Input value={form.no_bpjs_kesehatan} onChange={v => upd("no_bpjs_kesehatan", v)} /></Field>
            <Field label="BPJS Ketenagakerjaan"><Input value={form.no_bpjs_tk} onChange={v => upd("no_bpjs_tk", v)} /></Field>
          </Section>

          <Section title="Kontak Darurat">
            <Field label="Nama Kontak Darurat">
              <Input value={form.kontak_darurat_nama || ""} onChange={v => upd("kontak_darurat_nama", v)} tid="input-kd-nama" />
            </Field>
            <Field label="Hubungan">
              <Select value={form.kontak_darurat_hubungan || ""} onChange={v => upd("kontak_darurat_hubungan", v)} tid="input-kd-hubungan"
                options={[{ v: "", l: "— pilih hubungan —" }, ...HUBUNGAN_DARURAT.map(h => ({ v: h, l: h }))]} />
            </Field>
            <Field label="No. HP Kontak Darurat">
              <Input value={form.kontak_darurat_hp || ""} onChange={v => upd("kontak_darurat_hp", v)} tid="input-kd-hp" />
            </Field>
            <Field label="Alamat Kontak Darurat">
              <Input value={form.kontak_darurat_alamat || ""} onChange={v => upd("kontak_darurat_alamat", v)} tid="input-kd-alamat" />
            </Field>
          </Section>

          <EmployeeAccountSection
            form={form} upd={upd} isEdit={isEdit}
            permTemplates={permTemplates} pelaku={pelaku}
            Field={Field} Input={Input} Select={Select}
            LOGIN_ROLES={LOGIN_ROLES}
          />

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
            <button type="button" onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
            <button type="submit" data-testid="btn-save-employee" disabled={!bolehSimpan}
              title={bolehSimpan ? "" : "Daftar grade jabatan tidak tersedia — lihat peringatan di atas"}
              className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium disabled:bg-slate-300 disabled:cursor-not-allowed">Simpan</button>
          </div>
        </form>
        {guard.confirming && <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} />}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-display font-semibold text-slate-900 border-b border-slate-100 pb-2 mb-4">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <label className="flex flex-col gap-1.5"><span className="text-xs font-medium text-slate-600">{label}</span>{children}</label>;
}
function Input({ value, onChange, type = "text", required, tid, placeholder }) {
  return <input type={type} value={value ?? ""} required={required} data-testid={tid} placeholder={placeholder}
    onChange={e => onChange(e.target.value)}
    className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />;
}
function Select({ value, onChange, options, required, tid }) {
  return <select value={value ?? ""} onChange={e => onChange(e.target.value)} required={required} data-testid={tid}
    className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600/40">
    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>;
}

function CredsModal({ creds, onClose }) {
  const copyText = `Email: ${creds.email}\nPassword: ${creds.password}\nRole: ${creds.role}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="creds-modal">
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            <h3 className="font-display font-semibold text-sm">Akun Login Karyawan Aktif</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            Simpan dan bagikan kredensial ini ke karyawan. <b>Password hanya tampil sekali</b> — bisa direset lagi via Edit Karyawan.
          </div>
          <div className="space-y-2">
            <Row l="Email" v={creds.email} />
            <Row l="Password" v={creds.password} mono />
            <Row l="Role" v={creds.role} />
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button onClick={() => { navigator.clipboard.writeText(copyText); toast.success("Kredensial disalin"); }}
              data-testid="btn-copy-creds"
              className="flex-1 h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
              Salin Kredensial
            </button>
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 py-2 last:border-0">
      <span className="text-xs text-slate-500">{l}</span>
      <span className={`text-sm text-slate-900 ${mono ? "font-mono font-bold text-emerald-700" : "font-medium"}`}>{v}</span>
    </div>
  );
}
